/**
 * Clerk integration — provider + token bridging to our API client.
 *
 * When ``VITE_CLERK_PUBLISHABLE_KEY`` is empty (dev), we skip ClerkProvider
 * entirely and the API client falls back to the ``x-dev-user`` header.
 */

import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { useEffect, type ReactNode } from "react";
import { env } from "@/config/env";
import { setTokenGetter } from "@/services/apiClient";

type ClerkSetupProps = {
  children: ReactNode;
};

export function ClerkSetup({ children }: ClerkSetupProps) {
  if (!env.clerkPublishableKey) {
    // Dev mode — no Clerk. API client uses x-dev-user header automatically.
    return <>{children}</>;
  }

  return (
    <ClerkProvider
      publishableKey={env.clerkPublishableKey}
      appearance={{
        variables: {
          colorPrimary: "#f5b263",
          colorBackground: "#0c0e12",
          colorInputBackground: "#161a22",
          colorInputText: "#eef0f5",
          colorText: "#eef0f5",
          colorTextSecondary: "#a3abbd",
          borderRadius: "0.75rem",
          fontFamily: "Inter, system-ui, sans-serif",
        },
      }}
    >
      <ClerkTokenBridge />
      {children}
    </ClerkProvider>
  );
}

/**
 * Bridges Clerk's `getToken()` into our API client.
 * Runs as a sibling to the provider children so it mounts exactly once.
 */
function ClerkTokenBridge() {
  const { getToken, isSignedIn } = useAuth();

  useEffect(() => {
    setTokenGetter(async () => {
      if (!isSignedIn) return null;
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
  }, [getToken, isSignedIn]);

  return null;
}
